import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/Card';
import {
  Area, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import apiClient from '../../utils/api';

// The nightly sleep score (0–100) — a transparent composite of duration, deep+REM,
// efficiency and restfulness, computed server-side and served through the generic
// /api/health/series metric endpoint. The range is owned by the Sleep tab
// (SleepDataView) and passed in, so this trend moves in lockstep with the other
// sleep charts. No standalone selector or summary cards here.

// Green ≥85 (strong), gold ≥70 (solid), orange below (room to improve).
const toneColor = (s) => (s == null ? '#9ca3af' : s >= 85 ? '#10B981' : s >= 70 ? '#FFB800' : '#F0791A');

const SleepScoreChart = ({ inRange, spanDays = 30 }) => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/api/health/series', { params: { metric: 'sleep_score', days: 365 } })
      .then((r) => setPoints(r.data?.points || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => points.filter((p) => inRange(p.date)),
    [points, inRange],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!points.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-600 dark:text-gray-300">
            No sleep score yet. It's computed from your logged sleep — sync your wearable and it'll appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = filtered.map((p) => ({ date: p.date, score: p.value }));

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (spanDays <= 90) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const ScoreTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const label = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
    return (
      <div className="rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card px-3 py-2 shadow-lg">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-sm" style={{ color: toneColor(d.score) }}>
          Sleep score: <span className="font-bold">{d.score}</span>
        </p>
      </div>
    );
  };

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Sleep score trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFB800" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#FFB800" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" strokeOpacity={0.2} vertical={false} />
            <XAxis dataKey="date" tickFormatter={formatDate} interval={tickInterval}
                   tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#9ca3af' }}
                   axisLine={false} tickLine={false} width={36} />
            <Tooltip content={<ScoreTooltip />} />
            <ReferenceLine y={85} stroke="#10B981" strokeDasharray="4 4" strokeOpacity={0.6}
                           label={{ value: 'strong', position: 'right', fontSize: 11, fill: '#10B981' }} />
            <ReferenceLine y={70} stroke="#FFB800" strokeDasharray="4 4" strokeOpacity={0.5} />
            <Area type="monotone" dataKey="score" stroke="#FFB800" strokeWidth={2}
                  fill="url(#scoreFill)" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default SleepScoreChart;
